import { useEffect, useMemo, useState } from "react";
import {
  authorizationIdFromUrl,
  consentReturnPath,
  redirectUrlFromOAuthResult,
  scopeItems,
} from "../../oauth-flow.js";
import { AuthLayout } from "../components/AuthLayout";
import { getSupabaseClient } from "../lib/supabase";

const AUTH_TIMEOUT_MS = 15_000;

async function withTimeout<T>(
  promise: Promise<T>,
  message: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = globalThis.setTimeout(
      () => reject(new Error(message)),
      AUTH_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}

type ConsentDetails = {
  authorization_id: string;
  client: { name: string };
  redirect_uri: string;
  scope: string;
  [key: string]: unknown;
};

type ConsentState =
  | { kind: "loading" }
  | { kind: "consent"; details: ConsentDetails }
  | { kind: "failure"; message: string };

export function OAuthConsentPage() {
  const authorizationId = useMemo(
    () => authorizationIdFromUrl(globalThis.location.href),
    [],
  );
  const [state, setState] = useState<ConsentState>({ kind: "loading" });
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (!authorizationId) {
        setState({
          kind: "failure",
          message: "De authorization_id ontbreekt of is ongeldig.",
        });
        return;
      }
      const client = getSupabaseClient();
      try {
        const { data: userData, error: userError } = await withTimeout(
          client.auth.getUser(),
          "De sessiecontrole duurt te lang. Vernieuw de pagina.",
        );
        if (!active) return;
        if (userError || !userData.user) {
          const returnTo = consentReturnPath(authorizationId);
          globalThis.location.assign(
            `/login/?return_to=${encodeURIComponent(returnTo)}`,
          );
          return;
        }

        const { data: details, error } = await withTimeout(
          client.auth.oauth.getAuthorizationDetails(authorizationId),
          "De autorisatieaanvraag reageert niet. Start de koppeling opnieuw.",
        );
        if (!active) return;
        if (error || !details) {
          setState({
            kind: "failure",
            message: "De autorisatieaanvraag is ongeldig of verlopen.",
          });
          return;
        }
        if (!("authorization_id" in details)) {
          globalThis.location.assign(redirectUrlFromOAuthResult(details));
          return;
        }
        setState({ kind: "consent", details: details as ConsentDetails });
      } catch (error) {
        if (active) {
          setState({
            kind: "failure",
            message: error instanceof Error ? error.message : "Autorisatie niet mogelijk.",
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(decision: "approve" | "deny") {
    if (!authorizationId || state.kind !== "consent" || deciding) return;
    setDeciding(true);
    try {
      const client = getSupabaseClient();
      const { data, error } = decision === "approve"
        ? await client.auth.oauth.approveAuthorization(authorizationId)
        : await client.auth.oauth.denyAuthorization(authorizationId);
      if (error) {
        setState({
          kind: "failure",
          message: "De autorisatiebeslissing kon niet worden verwerkt. Start de koppeling opnieuw.",
        });
        return;
      }
      globalThis.location.assign(redirectUrlFromOAuthResult(data));
    } catch {
      setState({
        kind: "failure",
        message: "De autorisatiebeslissing kon niet worden verwerkt. Start de koppeling opnieuw.",
      });
    } finally {
      setDeciding(false);
    }
  }

  return (
    <AuthLayout>
      {state.kind === "loading" && (
        <section>
          <h1>Autorisatie controleren</h1>
          <p>Een ogenblik.</p>
        </section>
      )}
      {state.kind === "failure" && (
        <section>
          <h1>Autorisatie niet mogelijk</h1>
          <p className="error" role="alert">{state.message}</p>
        </section>
      )}
      {state.kind === "consent" && (
        <section>
          <h1>Toegang verlenen?</h1>
          <p>
            <strong>{state.details.client.name}</strong> vraagt toegang tot je DBVDW-account.
          </p>
          <p className="muted">
            Terugstuuradres: <span>{state.details.redirect_uri}</span>
          </p>
          <p>Gevraagde gegevens:</p>
          <ul>
            {scopeItems(state.details.scope).map((scope: string) => <li key={scope}>{scope}</li>)}
          </ul>
          <div className="actions">
            <button
              type="button"
              className="secondary"
              disabled={deciding}
              onClick={() => void decide("deny")}
            >
              Weigeren
            </button>
            <button
              type="button"
              disabled={deciding}
              onClick={() => void decide("approve")}
            >
              Toestaan
            </button>
          </div>
        </section>
      )}
    </AuthLayout>
  );
}
